import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { PAGE_SIZE } from '../constants';
import { getStoredPageSize } from '../hooks/useColumnConfig';
import { Button, Input, Modal, Popconfirm, Spin } from 'antd';
import { ReactSVG } from 'react-svg';
import queryString from 'query-string';
import AdminUsersTable from '../components/Tables/AdminUsersTable';
import AdminUserForm from '../components/Forms/AdminUserForm';
import { selectAllAdminUsers } from '../modules/admin-users/stores/slice';
import {
  doCreateAdminUser,
  doGetAdminUsers,
  doRemoveAdminUser,
  doUpdateAdminUser,
} from '../modules/admin-users/stores/thunk';
import { resetAdminUserTwoFactor } from '../modules/admin-users/api';
import { message } from 'antd';
import { SafetyOutlined } from '@ant-design/icons';

const initStateModal = {
  open: false,
  user: null,
  action: 'ide',
};

const AdminUsersPage = () => {
  const { t } = useTranslation('auth');
  const { t: tc } = useTranslation('common');
  const dispatch = useDispatch();
  const location = useLocation();
  const navigate = useNavigate();
  const isFetching = useSelector((state) => state.adminUser.isFetching);
  const status = useSelector((state) => state.adminUser.status);
  const action = useSelector((state) => state.adminUser.action);
  const metadata = useSelector((state) => state.adminUser.metadata);
  const adminUsers = useSelector((state) => selectAllAdminUsers(state));
  const [searchValue, setSearchValue] = useState('');
  const [searchParams] = useSearchParams();

  const [, setSelectedRowKeys] = useState([]);
  const [stateModal, setStateModal] = useState(initStateModal);
  const tableConfigRef = useRef({});

  const query = Object.fromEntries(searchParams);
  const currentPage = query.current || 1;
  // Page size lives under the gear (house rule) — the drawer persists it per
  // table; the URL still wins so shared links keep their pagination.
  const itemsPerPage = query.pageSize || getStoredPageSize('admin-users-columns', PAGE_SIZE);
  const pagination = {
    current: currentPage || 1,
    pageSize: Number(itemsPerPage),
    total: metadata.totalItems || adminUsers.length,
    showSizeChanger: false,
    showTotal: (total) => tc('totalItems', { total, entity: t('adminUsers') }),
  };

  const onSelectChange = (newSelectedRowKeys) => {
    setSelectedRowKeys(newSelectedRowKeys);
  };

  useEffect(() => {
    if (['create', 'update', 'remove']?.includes(action) && status === 'succeeded') {
      dispatch(doGetAdminUsers({ page: currentPage, take: itemsPerPage, ...query }));
      handleCancelModal();
    }
  }, [isFetching]);

  useEffect(() => {
    setSearchValue(query.q || '');
  }, []);

  useEffect(() => {
    dispatch(doGetAdminUsers({ page: currentPage, take: itemsPerPage, ...query }));
  }, []);

  const handleCancelModal = () => {
    setStateModal(initStateModal);
  };

  const pushTo = (pushProps) => {
    navigate({
      pathname: pushProps.path || location.pathname,
      search: queryString.stringify(pushProps.query || {}, {
        skipNull: true,
      }),
      state: pushProps.state,
    });
  };

  const handleSubmitModal = (data) => {
    if (stateModal.action === 'new') {
      dispatch(doCreateAdminUser(data));
    } else {
      data.id = stateModal.user.id;
      dispatch(doUpdateAdminUser(data));
    }
  };

  const handleFilter = (params, updatePath) => {
    delete params.pageSizeOptions;
    delete params.locale;
    if (updatePath) {
      pushTo({ query: params });
    }
    const newCurrentPage = Number(params?.current || 1);
    const newItemsPerPage = Number(params?.pageSize) || PAGE_SIZE;
    dispatch(
      doGetAdminUsers({
        page: params.current || newCurrentPage,
        take: newItemsPerPage,
        ...params,
      }),
    );
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      handleSearch && handleSearch();
    }
  };

  const handleChangeSearch = (e) => {
    setSearchValue(e.target.value);
  };

  const handleSearch = () => {
    handleFilter({ q: searchValue || '' }, true);
  };

  const handleTableChange = (params) => {
    const newParams = { ...query, ...params };
    handleFilter(newParams, true);
  };

  const onClickNew = () => {
    setStateModal({
      ...stateModal,
      open: true,
      user: null,
      action: 'new',
    });
  };

  const onClickEdit = (record) => {
    setStateModal({
      ...stateModal,
      open: true,
      user: record,
      action: 'edit',
    });
  };

  // OVO-159 — superadmin clears a locked-out admin's TOTP enrollment; the
  // user re-enrolls at next login (forced under REQUIRE_2FA_FOR_ADMINS).
  const onResetTwoFactor = async (record) => {
    const { error } = await resetAdminUserTwoFactor(record.id);
    if (error) {
      message.error(error?.message || 'Reset failed');
      return;
    }
    message.success(t('twoFactorResetDone', '2FA reset — the user will re-enroll at next login'));
    dispatch(doGetAdminUsers({ page: currentPage, take: itemsPerPage }));
  };

  const withAction = (record) => {
    return (
      <div className="actions">
        <Button type="link" className="edit" onClick={() => onClickEdit(record)}>
          <ReactSVG className="icon" src="/icons/edit.svg" />
        </Button>
        {record.totpEnabled && (
          <Popconfirm
            title={t('twoFactorResetConfirm', "Reset this user's two-factor authentication?")}
            onConfirm={() => onResetTwoFactor(record)}
            okText={tc('yes')}
            cancelText={tc('no')}
          >
            <Button type="link" title="Reset 2FA">
              <SafetyOutlined />
            </Button>
          </Popconfirm>
        )}
        <Popconfirm
          title={t('removeUserConfirm')}
          onConfirm={() => dispatch(doRemoveAdminUser({ id: record.id }))}
          okText={tc('yes')}
          cancelText={tc('no')}
        >
          <Button type="link" className="delete" danger>
            <ReactSVG className="icon" src="/icons/more.svg" />
          </Button>
        </Popconfirm>
      </div>
    );
  };

  return (
    <Spin spinning={isFetching}>
      <div className="DriversPage Content">
        <div className="ContentHeader">
          <div className="HeaderTitle">{t('adminUsers')}</div>
          <div className="HeaderToolBar">
            <div className="ToolBarButtons">
              <Button className="CreateButton ButtonPrimary" type="primary" size="small" onClick={onClickNew}>
                {t('createNewAdmin')}
                <ReactSVG className="icon" src="/icons/plus.svg" />
              </Button>
            </div>
          </div>
        </div>
        <div className="page-header-table">
          <div className="search">
            <Input
              onChange={handleChangeSearch}
              onBlur={handleSearch}
              placeholder={tc('search')}
              onKeyDown={handleKeyDown}
              suffix={<img src="/icons/search.svg" />}
              disabled={isFetching}
              value={searchValue}
            />
          </div>
          <div className="buttons">
            <Button
              type="text"
              onClick={() => tableConfigRef.current?.openDrawer?.()}
              style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}
            >
              <ReactSVG className="icon" src="/icons/settings.svg" />
            </Button>
          </div>
        </div>

        <div className="ContentMain">
          <AdminUsersTable
            dataSource={adminUsers}
            pagination={pagination}
            withAction={withAction}
            rowKey="id"
            isFetching={isFetching}
            onSelectChange={onSelectChange}
            onChange={handleTableChange}
            configRef={tableConfigRef}
            onPageSizeChange={(size) => handleTableChange({ pageSize: size, current: 1 })}
          />
        </div>

        <Modal open={stateModal.open} onCancel={handleCancelModal} footer={[]} width={792} forceRender>
          <AdminUserForm
            handleSubmit={handleSubmitModal}
            handleCancel={handleCancelModal}
            user={stateModal.user}
            action={stateModal.action}
            isFetching={isFetching}
          />
        </Modal>
      </div>
    </Spin>
  );
};

export default AdminUsersPage;
